import { FC, useMemo, useState } from 'react';
import { divide, multiply, sum } from 'lodash';
import { Button, Skeleton, Tooltip } from '@mui/material';
import AddLocationAltIcon from '@mui/icons-material/AddLocationAlt';
import NumberInput from 'src/components/Items/input/NumberInput';
import ArticlesModal from '../Modal';
import UpdateVanModal from 'src/pages/Van/Modal/Update';
import { useActions } from 'src/hooks/useActions';
import { useTypedSelector } from 'src/hooks/useTypeSelector';
import useIsMobile from 'src/hooks/useIsMobile';
import { dineroFormat, dineroSubtractAmount } from 'src/common/utils/dataFormat';
import { createVanURL } from 'src/common/utils/others';
import { IExtra, ManualType } from 'src/models/IOrder';
import { IVan, IVanFetch } from 'src/models/IVan';
import Icons from 'src/common/icons';
import './styles.scss';
import './media.scss';

interface IProps {
  item: IVanFetch | IExtra;
  title: ManualType;
  days?: number;
  index?: number;
}

const CartItem: FC<IProps> = ({ item, title, index }) => {
  const {
    order,
    isLoading: { get }
  } = useTypedSelector(state => state.order);
  const { id, fee_list, sub_location } = order!;
  const { updateOrder } = useActions();
  const isMobile = useIsMobile();
  const subId = process.env.REACT_APP_NODE_ENV ? 18 : 27;
  const [openPrice, setOpenPrice] = useState(false);
  const [openVanModal, setOpenVanModal] = useState(false);
  const [openExtrasModal, setOpenExtrasModal] = useState(false);

  const [price, setPrice] = useState(0);

  const onSubmit = () => {
    if (title === 'van') {
      updateOrder({ price: Math.round(multiply(price, 100)), id });
    } else {
      const fee = fee_list[index!];
      const editedFeeList = [...fee_list];
      editedFeeList[index!] = { ...fee, price: Math.round(multiply(price, 100)) };
      updateOrder({ fee_list: editedFeeList, id });
    }

    setOpenPrice(false);
  };

  useMemo(() => {
    if (title === 'van') {
      const newPrice = dineroSubtractAmount(
        order!.price,
        Math.round(sum(fee_list.map(i => i.price))),
        item.currency
      );
      setPrice(divide(newPrice, 100));
    } else {
      setPrice(divide(item.price, 100));
    }
  }, [open, order]);

  const _renderPrice = (item: IVanFetch | IExtra, title: 'van' | 'extras') =>
    title === 'van' ? (
      openPrice ? (
        <div className='order-cart__item__price'>
          <div className='order-cart__item__price__action' onClick={() => setOpenPrice(false)}>
            <Icons.Cross />
          </div>
          <NumberInput
            data={price}
            setData={setPrice}
            variant='outlined'
            style={{ height: '20px' }}
          />
          <div className='order-cart__item__price__action' onClick={onSubmit}>
            <Icons.ArrowDown />
          </div>
        </div>
      ) : (
        <div className='articles__price__all' onClick={() => setOpenPrice(true)}>
          {dineroFormat(Math.round(multiply(price, 100)), item.currency)}
        </div>
      )
    ) : openPrice ? (
      <div className='order-cart__item__price'>
        <div className='order-cart__item__price__action' onClick={() => setOpenPrice(false)}>
          <Icons.Cross />
        </div>
        <NumberInput
          data={price}
          setData={setPrice}
          variant='outlined'
          style={{ height: '20px' }}
          unlimit
        />
        <div className='order-cart__item__price__action' onClick={onSubmit}>
          <Icons.ArrowDown />
        </div>
      </div>
    ) : (
      <div className='order-cart__item__price' onClick={() => setOpenPrice(true)}>
        {dineroFormat(item.price, item.currency)}
      </div>
    );

  return title === 'van' ? (
    <>
      <div className='articles'>
        <div className='articles__info'>
          <p>
            <b>Plates: </b>
            <span className='articles__text'>{(item as IVan).license_plate}</span>
          </p>
          <p>
            <b>Van ID: </b>
            <span className='articles__text'>{(item as IVan).model}</span>
          </p>
        </div>
        <div className='articles__container'>
          <div className='articles__img'>
            {get ? (
              <Skeleton width='100%' height='100%' />
            ) : isMobile ? (
              <img src={(item as IVan).photos?.exterior[0]} alt='van' loading='lazy' />
            ) : (
              <Tooltip title='Edit van'>
                <img
                  src={(item as IVan).photos?.exterior[0]}
                  alt='van'
                  loading='lazy'
                  onClick={() => setOpenVanModal(true)}
                />
              </Tooltip>
            )}
          </div>
          <div className='articles__price'>
            <div>
              {_renderPrice(item, 'van')}
              <p className='articles__price__rate'>
                Listed Rate&nbsp;
                {dineroFormat(item.price, item.currency)}
                /day
              </p>
            </div>
            <p className='articles__text'>
              {(item as IVan).mark}
              <a
                href={createVanURL(item as IVanFetch)}
                target='_blank'
                className='articles__text__link'
              >
                Here.
              </a>
            </p>
          </div>
        </div>
        <div className='articles__actions'>
          <p className='articles__text'>Tip: Click amounts to edit rates</p>
          <Button children='Add Services' onClick={() => setOpenExtrasModal(true)} />
        </div>
      </div>
      <ArticlesModal
        open={openExtrasModal}
        handleClose={() => setOpenExtrasModal(false)}
        order={order!}
      />
      <UpdateVanModal
        van={item as IVanFetch}
        handleClose={() => setOpenVanModal(false)}
        open={openVanModal}
      />
    </>
  ) : (
    <div className='order-cart__item'>
      <div className='order-cart__item__img'>
        {(item as IExtra).id === subId ? (
          <AddLocationAltIcon fontSize='large' />
        ) : (
          <img src={(item as IExtra).logo} alt='extra' loading='lazy' />
        )}
      </div>
      <div className='order-cart__item__info'>
        <div className='order-cart__item__info__title'>
          {(item as IExtra).id === subId ? sub_location : (item as IExtra).name}
        </div>
        {/* <div className='order-cart__item__info__description'>
          {(item as IExtra).title}
        </div> */}
      </div>
      {_renderPrice(item, 'extras')}
    </div>
  );
};
export default CartItem;
